import {
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    AfterViewInit,
    OnDestroy,
} from '@angular/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import {
    catchError,
    finalize,
    map,
    Observable,
    of,
    Subject,
    takeUntil,
    tap,
} from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { Address } from '../../../models/domain/Address';
import { State } from '../../../models/domain/State';
import { BaseSearchOptions } from '../../../models/core/table/BaseSearchOptions';
import { CustomizerSettingsService } from '../../../customizer-settings/customizer-settings.service';
import { AddressService } from '../../../services/address.service';
import { LoadingService } from '../../../services/loading.service';
import { StateService } from '../../../services/state.service';
import { ObjectUtility } from '../../../utilities/ObjectUtility';
import { whitespaceValidator } from '../../../validators/ValidationService';
import { CityService } from '../../../services/city.service';
import { City } from '../../../models/domain/City';
import { Country } from '../../../models/domain/Country';
import { CountryService } from '../../../services/country.service';

@Component({
    selector: 'app-address-details',
    templateUrl: './address-details.component.html',
    styleUrl: './address-details.component.scss',
    imports: [ReactiveFormsModule, CommonModule],
})
export class AddressDetailsComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    @Output() classApplied = new EventEmitter<boolean>();
    @Output() saveConfirmationEvent = new EventEmitter<boolean>();
    @Input() currentAddressId = 0;
    // isToggled
    isToggled = false;
    frmGroup!: FormGroup;

    private destroy$: Subject<boolean> = new Subject<boolean>();
    public addressOriginal = new Address();
    public address: Address = new Address();
    public titleText = '';
    public titleButton = '';
    public errorMessage = '';
    isSubmitted = false;

    //#region Dropdown DataBind
    public country$!: Observable<Country[]>;
    public country!: Country[];

    public state$!: Observable<State[]>;
    public state!: State[];

    public city$!: Observable<City[]>;
    public city!: City[];

    public pageIndex = 0;
    public pageSize = 1000;
    public searchOptions!: BaseSearchOptions;
    // #endregion

    constructor(
        public themeService: CustomizerSettingsService,
        private formBuilder: FormBuilder,
        private addressService: AddressService,
        private toasterService: ToastrService,
        private loadingService: LoadingService,
        private countryService: CountryService,
        private stateService: StateService,
        private cityService: CityService,
    ) {
        this.initialize();
    }

    public ngOnInit(): void {
        if (this.currentAddressId > 0) {
            this.getDropdownData();
        } else {
            this.currentAddressId = 0;
            this.getDropdownData();
            const newAddress = new Address();
            this.address = ObjectUtility.deepClone(newAddress);
            this.addressOriginal = ObjectUtility.deepClone(newAddress);
            this.setForm(this.address);
        }
    }

    private initialize(): void {
        this.themeService.isToggled$.subscribe((isToggled) => {
            this.isToggled = isToggled;
        });

        this.searchOptions = new BaseSearchOptions(
            undefined,
            'name',
            0,
            this.pageIndex,
            this.pageSize,
        );
        this.isSubmitted = false;
        this.createForm();
    }

    public ngAfterViewInit(): void {
        this.frmGroup.controls['addressLine1Control'].valueChanges.subscribe(
            (value) => {
                this.address.addressLine1 = value;
            },
        );

        this.frmGroup.controls['addressLine2Control'].valueChanges.subscribe(
            (value) => {
                this.address.addressLine2 = value;
            },
        );

        this.frmGroup.controls['landmarkControl'].valueChanges.subscribe(
            (value) => {
                this.address.landmark = value;
            },
        );

        this.frmGroup.controls['pinCodeControl'].valueChanges.subscribe(
            (value) => {
                this.address.pinCode = value;
            },
        );

        this.frmGroup.controls['countryControl'].valueChanges.subscribe(
            (value) => {
                this.address.country = value;
            },
        );

        this.frmGroup.controls['stateControl'].valueChanges.subscribe(
            (value) => {
                this.address.state = value;
            },
        );

        this.frmGroup.controls['cityControl'].valueChanges.subscribe(
            (value) => {
                this.address.city = value;
            },
        );
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    get f() {
        return this.frmGroup.controls;
    }

    private createForm(): void {
        this.frmGroup = this.formBuilder.group({
            addressLine1Control: new FormControl('', [
                Validators.required,
                whitespaceValidator(),
            ]),
            addressLine2Control: new FormControl(null, [whitespaceValidator()]),
            landmarkControl: new FormControl('', [whitespaceValidator()]),
            pinCodeControl: new FormControl(Validators.required, [
                Validators.required,
            ]),
            countryControl: new FormControl(Validators.required, [
                Validators.required,
            ]),
            stateControl: new FormControl(Validators.required, [
                Validators.required,
            ]),
            cityControl: new FormControl(Validators.required, [
                Validators.required,
            ]),
        });
    }

    private setForm(address: Address): void {
        if (address) {
            this.frmGroup.patchValue({
                addressLine1Control: address.addressLine1,
                addressLine2Control: address.addressLine2,
                landmarkControl: address.landmark,
                pinCodeControl: address.pinCode,
                countryControl: address.country,
                stateControl: address.state,
                cityControl: address.city,
            });
        }
        this.titleText = `${this.currentAddressId === 0 ? 'Add' : 'Edit'} Address`;
        this.titleButton = `${this.currentAddressId === 0 ? 'Create' : 'Update'}`;
    }

    public onSave(): void {
        this.isSubmitted = true;
        if (this.frmGroup.valid) {
            this.loadingService.showLoading();
            this.addEditAddress();
        }
    }

    private addEditAddress(): void {
        if (this.address?.id === 0) {
            this.addressService
                .create(this.sanitizeService(this.address))
                .pipe(
                    catchError((err: any) => {
                        if (err && err.error) {
                            this.isSubmitted = false;
                            if (err.error.isCustom) {
                                this.errorMessage = err.error.message;
                                this.toasterService.warning(err.error.message);
                            } else {
                                this.toasterService.error(
                                    'Something went wrong!',
                                );
                            }
                        } else {
                            this.isSubmitted = false;
                            this.toasterService.error('Something went wrong!');
                        }
                        this.loadingService.hideLoading();

                        return of(null);
                    }),
                    finalize(() => {
                        this.loadingService.hideLoading();
                    }),
                    takeUntil(this.destroy$),
                )
                .subscribe((response) => {
                    if (response) {
                        this.isSubmitted = false;
                        this.address =
                            response?.body != null
                                ? response?.body
                                : new Address();
                        this.saveConfirmationEvent.emit(true);
                        this.toggleClass();
                        this.toasterService.success('Save successfully');
                    }
                    this.loadingService.hideLoading();
                });
        } else {
            this.addressService
                .update(this.sanitizeService(this.address))
                .pipe(
                    catchError((err: any) => {
                        if (err && err.error) {
                            this.isSubmitted = false;
                            if (err.error.isCustom) {
                                this.errorMessage = err.error.message;
                                this.toasterService.warning(err.error.message);
                            } else {
                                this.toasterService.error(
                                    'Something went wrong!',
                                );
                            }
                        } else {
                            this.isSubmitted = false;
                            this.toasterService.error('Something went wrong!');
                        }
                        return of(null);
                    }),
                    takeUntil(this.destroy$),
                    // Ensure loading is always hidden, even if error is not caught
                    finalize(() => {
                        this.loadingService.hideLoading();
                    }),
                )
                .subscribe((response) => {
                    if (response) {
                        this.isSubmitted = false;
                        this.address =
                            response?.body != null
                                ? response?.body
                                : new Address();
                        this.saveConfirmationEvent.emit(true);
                        this.toasterService.success('Update successfully');
                        this.toggleClass();
                    }
                });
        }
    }

    public toggleClass(): void {
        this.classApplied.emit(false);
    }

    public dataHasChanged(): boolean {
        const baseEqual = ObjectUtility.deepEqual(
            this.address,
            this.addressOriginal,
        );
        return !baseEqual;
    }

    public formIsValid(): boolean {
        const isValid =
            this.frmGroup.controls['addressLine1Control'].valid &&
            this.frmGroup.controls['pinCodeControl'].valid &&
            this.frmGroup.controls['countryControl'].valid &&
            this.frmGroup.controls['stateControl'].valid &&
            this.frmGroup.controls['cityControl'].valid;
        return isValid;
    }

    //#region Dropdown compare

    public countryComparer(t1: Country, t2: Country): boolean {
        return t1 && t2 ? t1.id === t2.id || t1.name === t2.name : t1 === t2;
    }

    public stateComparer(t1: State, t2: State): boolean {
        return t1 && t2 ? t1.id === t2.id || t1.name === t2.name : t1 === t2;
    }

    public cityComparer(t1: City, t2: City): boolean {
        return t1 && t2 ? t1.id === t2.id || t1.name === t2.name : t1 === t2;
    }

    // #endregion

    private sanitizeService(address: Address): Address {
        address.country = null;
        address.countryId = this.frmGroup.controls['countryControl'].value.id;

        address.state = null;
        address.stateId = this.frmGroup.controls['stateControl'].value.id;

        address.city = null;
        address.cityId = this.frmGroup.controls['cityControl'].value.id;

        address.business = null;

        return address;
    }

    private getDropdownData() {
        this.loadingService.showLoading();

        this.country$ = this.countryService.get(this.searchOptions).pipe(
            map((result: any) => {
                this.loadingService.hideLoading();
                return result.body.values;
            }),
            catchError(() => {
                this.loadingService.hideLoading();
                return of([]);
            }),
        );

        this.getAddressById(this.currentAddressId);
    }

    public getAddressById(addressId: number) {
        if (addressId) {
            this.loadingService.showLoading();
            this.addressService
                .getById(addressId)
                .pipe(takeUntil(this.destroy$))
                .subscribe((result) => {
                    if (result) {
                        this.address = ObjectUtility.deepClone(result);
                        this.addressOriginal = ObjectUtility.deepClone(result);
                        this.GetAllDropDownData(this.address);
                        this.setForm(this.address);
                    }
                    this.loadingService.hideLoading();
                });
        }
    }

    public GetAllDropDownData(address: Address) {
        this.getStateByCountry(address.countryId);
        this.getCityByState(address.stateId);
    }

    public getStateByCountry(countryId: number | undefined): void {
        if (countryId && countryId > 0) {
            this.loadingService.showLoading();

            this.stateService
                .getStateByCountry(countryId)
                .pipe(
                    takeUntil(this.destroy$),
                    tap((response: any) => {
                        this.state = response.body ?? [];

                        const selectedState =
                            this.state.find(
                                (state) => state.id === this.address.stateId,
                            ) || null;
                        this.frmGroup.patchValue({
                            stateControl: selectedState,
                        });
                        this.address.state = selectedState;
                    }),
                    catchError((error) => {
                        this.toasterService.error('Failed to load states.');
                        this.state = [];
                        this.frmGroup.patchValue({ stateControl: null });
                        return of(null); // Return a safe fallback to keep the stream alive
                    }),
                    finalize(() => {
                        this.loadingService.hideLoading();
                    }),
                )
                .subscribe(); // Minimal `subscribe()` required to trigger execution
        } else {
            this.state = [];
            this.frmGroup.patchValue({ stateControl: null });
        }
    }

    public getCityByState(stateId: number | undefined): void {
        if (stateId && stateId > 0) {
            this.loadingService.showLoading();

            this.cityService
                .getCityByState(stateId)
                .pipe(
                    takeUntil(this.destroy$),
                    tap((response: any) => {
                        this.city = response.body ?? [];

                        const selectedCity =
                            this.city.find(
                                (city) => city.id === this.address.cityId,
                            ) || null;

                        this.frmGroup.patchValue({ cityControl: selectedCity });
                        this.address.city = selectedCity;
                    }),
                    catchError((error) => {
                        this.toasterService.error('Failed to load cities.');
                        this.city = []; // Clear the city array on error
                        this.frmGroup.patchValue({ cityControl: null }); // Reset the city control
                        return of(null); // Return a safe fallback to keep the stream alive
                    }),
                    finalize(() => {
                        this.loadingService.hideLoading(); // Ensure loading is hidden
                    }),
                )
                .subscribe(); // Minimal `subscribe()` required to trigger execution
        } else {
            this.city = []; // Clear the city array if no state is selected
            this.frmGroup.patchValue({ cityControl: null }); // Reset the city control
        }
    }

    /*public getStateByCountry(countryId: number | undefined) {
    if (countryId && countryId > 0) {
      this.state$ = this.stateService.getStateByCountry(countryId).pipe(
        map((response: any) => {
          const states = response.body?.values ?? [];
          const firstState = states[0] || null;
          this.frmGroup.patchValue({ stateControl: firstState }); // Patch the first state
          this.address.state = firstState; // Set the state in the address object
          return states; // Return the states for the dropdown
        }),
        catchError(() => {
          this.toasterService.error('Failed to load states.');
          return of([]); // Return an empty array on error
        }),
        finalize(() => {
          this.loadingService.hideLoading(); // Ensure loading is hidden
        })
      );

      // this.stateService.getStateByCountry(countryId).subscribe(response => {
      //   this.loadingService.showLoading();
      //   this.state = response.body?.values ?? [];
      //   const firstState = this.state[0] || '';
      //   this.frmGroup.patchValue({ stateControl: firstState });
      //   this.loadingService.hideLoading();
      // });
    } else {
      this.state$ = of([]); // Clear the state dropdown if no country is selected
      this.frmGroup.patchValue({ stateControl: null }); // Reset the state control
    }
  }*/

    /*public getCityByState(stateId: number | undefined) {
    if (stateId && stateId > 0) {
      this.city$ = this.cityService.getCityByState(stateId).pipe(
        map((response: any) => {
          const city = response.body?.values ?? [];
          const firstCity = city[0] || null;
          this.frmGroup.patchValue({ cityControl: firstCity }); // Patch the first city
          this.address.city = firstCity; // Set the city in the address object
          return city; // Return the cities for the dropdown
        }),
        catchError(() => {
          this.toasterService.error('Failed to load cities.');
          return of([]); // Return an empty array on error
        }),
        finalize(() => {
          this.loadingService.hideLoading(); // Ensure loading is hidden
        })
      );

      // this.cityService.getCityByState(stateId).subscribe(response => {
      //   this.loadingService.showLoading();
      //   this.city = response.body?.values ?? [];
      //   const firstCity = this.city[0] || '';
      //   this.frmGroup.patchValue({ cityControl: firstCity });
      //   this.loadingService.hideLoading();
      // });
    } else {
      this.city$ = of([]); // Clear the city dropdown if no state is selected
      this.frmGroup.patchValue({ cityControl: null }); // Reset the city control
    }
  }*/

    public onCountryChange(value: Country) {
        if (value) {
            this.getStateByCountry(value.id);
        }
    }

    public onStateChange(value: State) {
        if (value) {
            this.getCityByState(value.id);
        }
    }
}
